import { Fragment, useEffect, useMemo, useState } from "react";
import { API_BASE, api, authHeaders, downloadAdminFile, getAdminToken, uploadKnowledgeBase } from "./api";
import {
  completeNewPassword,
  fetchCognitoConfig,
  hasStoredAuth,
  loginWithBreakglassToken,
  loginWithPassword,
  MeResponse,
  refreshSessionIfNeeded,
  signOut,
  type CognitoPublicConfig,
} from "./auth";
import {
  AnalysisResult,
  AnalyticsSummary,
  AnalyticsTrace,
  AppRole,
  ModelRate,
  ControlUser,
  DashboardData,
  FaqCatalog,
  FaqEntry,
  Flags,
  JudgeRouting,
  LlmProfile,
  PeakWindowUtc,
  View,
} from "./types";

type NavItem = { id: View; label: string; icon: string; roles: AppRole[] };

const NAV: NavItem[] = [
  { id: "overview", label: "Overview", icon: "⌂", roles: ["super-admin", "admin", "user"] },
  { id: "ask", label: "Ask", icon: "◇", roles: ["super-admin", "admin", "user"] },
  { id: "knowledge", label: "Knowledge", icon: "▤", roles: ["super-admin", "admin", "user"] },
  { id: "models", label: "Models", icon: "◈", roles: ["super-admin"] },
  { id: "controls", label: "Controls", icon: "◉", roles: ["super-admin"] },
  { id: "analytics", label: "Analytics", icon: "▥", roles: ["super-admin"] },
  { id: "users", label: "Users", icon: "☺", roles: ["super-admin", "admin"] },
];

function isAdminPlus(role: AppRole): boolean {
  return role === "super-admin" || role === "admin";
}

/** Alamo go-live recommended runtime posture. */
const FLAG_META: Record<
  keyof Flags,
  { label: string; blurb: string; group: "core" | "channels" | "ops"; recommended: boolean }
> = {
  assistantEnabled: {
    label: "Assistant master switch",
    blurb: "Kill switch for /analyze and the Chrome extension. Keep ON in production.",
    group: "core",
    recommended: true,
  },
  hybridMatchingEnabled: {
    label: "Hybrid matching (L1 lexical → L2 Voyage)",
    blurb: "Fast FAQ match without calling the LLM when confidence is high. Required for cost/quality.",
    group: "core",
    recommended: true,
  },
  llmMatchingEnabled: {
    label: "LLM judge (L3 on ambiguity)",
    blurb: "DeepSeek/OpenAI picks among top FAQ candidates when hybrid is unsure. Not a replacement for hybrid.",
    group: "core",
    recommended: true,
  },
  mockFallbackEnabled: {
    label: "Graceful fallback if LLM fails",
    blurb: "If the judge API errors, still return the best hybrid FAQ hit instead of failing the request.",
    group: "core",
    recommended: true,
  },
  airbnbExtractionEnabled: {
    label: "Airbnb channel",
    blurb: "Allow analysis when the extension reads Airbnb guest messages. Primary Alamo channel — keep ON.",
    group: "channels",
    recommended: true,
  },
  autoAnalyzeEnabled: {
    label: "Auto-analyze in extension",
    blurb: "Run analysis when the popup opens on a guest thread. Hosts can still Re-analyze manually if OFF.",
    group: "channels",
    recommended: true,
  },
  teamsExtractionEnabled: {
    label: "Microsoft Teams channel",
    blurb: "Allow analysis on Teams pages. Only needed if hosts use Teams for guests — safe OFF if unused.",
    group: "channels",
    recommended: false,
  },
  analyticsEnabled: {
    label: "Analytics recording",
    blurb: "Write analyze/judge/embed traces for the Analytics page (super-admin). Keep ON for ops visibility.",
    group: "ops",
    recommended: true,
  },
  debugLoggingEnabled: {
    label: "Verbose HTTP debug logs",
    blurb: "Extra request logging in CloudWatch. Keep OFF unless diagnosing a production issue.",
    group: "ops",
    recommended: false,
  },
};

function App() {
  const [bootstrapping, setBootstrapping] = useState(true);
  const [cognitoConfig, setCognitoConfig] = useState<CognitoPublicConfig | null>(null);
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [view, setView] = useState<View>("overview");
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState("");

  const role: AppRole = me?.role ?? "user";
  const navItems = useMemo(() => NAV.filter((item) => item.roles.includes(role)), [role]);

  useEffect(() => {
    if (!navItems.some((item) => item.id === view)) setView("overview");
  }, [role, view, navItems]);

  async function loadMeAndDashboard() {
    const profile = await api<MeResponse>("/api/admin/me");
    setMe(profile);
    setData(await api<DashboardData>("/api/admin/dashboard"));
    setError("");
    setAuthed(true);
  }

  async function refresh() {
    try {
      await loadMeAndDashboard();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Backend unavailable";
      setError(message);
      if (/Unauthorized|Forbidden/i.test(message)) {
        setAuthed(false);
        setMe(null);
        setData(null);
      }
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const config = await fetchCognitoConfig();
        setCognitoConfig(config);
        if (hasStoredAuth()) {
          if (config.cognitoEnabled) await refreshSessionIfNeeded(config);
          await loadMeAndDashboard();
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not start control center");
      } finally {
        setBootstrapping(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!navItems.some((item) => item.id === view)) setView("overview");
  }, [role, view, navItems]);

  function handleSignedIn() {
    void refresh();
  }

  function handleSignOut() {
    signOut();
    setAuthed(false);
    setMe(null);
    setData(null);
    setError("");
  }

  if (bootstrapping) {
    return <div className="login-page"><div className="login-card"><strong>Starting control center…</strong></div></div>;
  }

  if (!authed) {
    return (
      <LoginScreen
        config={cognitoConfig}
        onSignedIn={handleSignedIn}
        initialError={error}
      />
    );
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">
            <img src={`${import.meta.env.BASE_URL}brand/alamo-logo.png`} alt="Alamo City" />
          </span>
          <div>
            <strong>Alamo City Digital Assistant</strong>
            <small>Control Center</small>
          </div>
        </div>
        <nav>
          {navItems.map((item) => (
            <button
              key={item.id}
              className={view === item.id ? "nav active" : "nav"}
              onClick={() => setView(item.id)}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer identity">
          <div className="identity-row">
            <span className={`role-badge ${role}`}>{role}</span>
            <button type="button" className="text-button signout" onClick={handleSignOut}>Sign out</button>
          </div>
          <strong className="identity-email" title={me?.email}>{me?.email}</strong>
          <span className="status-line"><span className="status-dot" />Hosted control plane</span>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <div className="eyebrow">
              {role === "super-admin" ? "Operations" : role === "admin" ? "Workspace" : "Operator"}
            </div>
            <h1>{navItems.find((x) => x.id === view)?.label}</h1>
          </div>
        </header>
        {error && <div className="alert error">{error}</div>}
        {!data ? (
          <div className="empty">Loading workspace…</div>
        ) : (
          <>
            {view === "overview" && <Overview data={data} role={role} onNavigate={setView} />}
            {view === "ask" && <Playground data={data} role={role} onRefresh={refresh} />}
            {view === "knowledge" && <KnowledgePage data={data} role={role} onRefresh={refresh} />}
            {view === "models" && role === "super-admin" && <LlmManager data={data} onRefresh={refresh} />}
            {view === "controls" && role === "super-admin" && <ControlsPage flags={data.flags} onRefresh={refresh} />}
            {view === "analytics" && role === "super-admin" && <AnalyticsPage />}
            {view === "users" && isAdminPlus(role) && <UsersPage meRole={role} />}
          </>
        )}
      </main>
    </div>
  );
}

function LoginScreen({
  config,
  onSignedIn,
  initialError,
}: {
  config: CognitoPublicConfig | null;
  onSignedIn: () => void;
  initialError?: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [token, setToken] = useState(getAdminToken());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError ?? "");

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    if (!config?.cognitoEnabled || !config.cognitoClientId) {
      setError("Cognito is not enabled on this API. Use Advanced breakglass token.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await loginWithPassword(email, password, config);
      if (result.newPasswordRequired) {
        setChallengeSession(result.newPasswordRequired.session);
        setError("Set a new permanent password to finish sign-in.");
        return;
      }
      onSignedIn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  async function submitNewPassword(event: React.FormEvent) {
    event.preventDefault();
    if (!config || !challengeSession) return;
    setBusy(true);
    setError("");
    try {
      await completeNewPassword(email, newPassword, challengeSession, config);
      setChallengeSession(null);
      onSignedIn();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Password update failed");
    } finally {
      setBusy(false);
    }
  }

  function submitToken(event: React.FormEvent) {
    event.preventDefault();
    loginWithBreakglassToken(token);
    onSignedIn();
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={challengeSession ? submitNewPassword : submitPassword}>
        <div className="brand login-brand">
          <span className="brand-mark">
            <img src={`${import.meta.env.BASE_URL}brand/alamo-logo.png`} alt="Alamo City" />
          </span>
          <div>
            <strong>Alamo City Digital Assistant</strong>
            <small>Sign in to the control center</small>
          </div>
        </div>
        {error && <div className="alert error">{error}</div>}
        {!challengeSession ? (
          <>
            <label className="field">
              <span>Email</span>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="field">
              <span>Password</span>
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            <button className="primary" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
          </>
        ) : (
          <>
            <label className="field">
              <span>New password</span>
              <input type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12} />
              <small>At least 12 characters, upper, lower, and a number.</small>
            </label>
            <button className="primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Set password & continue"}</button>
          </>
        )}
        <button type="button" className="link advanced-toggle" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "Hide advanced" : "Advanced: API token"}
        </button>
        {showAdvanced && (
          <form className="breakglass" onSubmit={submitToken}>
            <label className="field">
              <span>Breakglass bearer token</span>
              <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="ADMIN_API_TOKEN" />
            </label>
            <button className="secondary" type="submit" disabled={!token.trim()}>Use token</button>
          </form>
        )}
      </form>
    </div>
  );
}

function Overview({ data, role, onNavigate }: { data: DashboardData; role: AppRole; onNavigate: (view: View) => void }) {
  const judge = data.resolvedJudge;
  const faqCount = data.faq?.entries ?? 0;
  const propertyCount = data.faq?.properties?.length ?? 0;
  const assistantOn = data.flags.assistantEnabled;
  const peak = Boolean(judge?.inPeakWindow);
  const isSuper = role === "super-admin";

  if (!isSuper) {
    return (
      <div className="stack overview-page overview-operator">
        <section className="hero overview-hero overview-hero-operator">
          <div className="overview-hero-copy">
            <span className={`pill ${assistantOn ? "pill-live" : "pill-off"}`}>
              {assistantOn ? "READY" : "PAUSED"}
            </span>
            <h2>Alamo City Digital Assistant</h2>
            <p>
              {role === "user"
                ? "Practice guest questions and confirm the curated answers before you reply on Airbnb."
                : "Keep the knowledge base current, invite operators, and verify guest answers in Ask."}
            </p>
            <div className="overview-cta-row">
              <button type="button" className="primary" onClick={() => onNavigate("ask")}>
                Ask a guest question
              </button>
              <button type="button" className="secondary ghost-on-dark" onClick={() => onNavigate("knowledge")}>
                {role === "admin" && faqCount === 0 ? "Import knowledge" : "Open knowledge"}
              </button>
            </div>
          </div>
        </section>

        <div className="overview-status overview-status-operator" aria-label="Workspace status">
          <div className="overview-status-item">
            <span>Guest FAQ</span>
            <strong>{faqCount.toLocaleString()}</strong>
            <small>{propertyCount ? `${propertyCount} properties live` : "Waiting on knowledge import"}</small>
          </div>
          <div className="overview-status-item">
            <span>Assistant</span>
            <strong>{assistantOn ? "On" : "Off"}</strong>
            <small>{assistantOn ? "Accepting guest analysis" : "Paused by an administrator"}</small>
          </div>
          <div className="overview-status-item">
            <span>Your focus</span>
            <strong>{role === "admin" ? "Knowledge" : "Ask"}</strong>
            <small>{role === "admin" ? "Publish FAQ · invite users" : "Test replies · browse FAQ"}</small>
          </div>
        </div>

        {faqCount === 0 && (
          <section className="overview-empty">
            <div>
              <h3>Knowledge not published yet</h3>
              <p>
                {role === "admin"
                  ? "Import the Alamo knowledge-base Excel on Knowledge so guest analysis can return answers."
                  : "Ask an admin to import the knowledge base before testing guest questions."}
              </p>
            </div>
            {role === "admin" && (
              <button type="button" className="primary" onClick={() => onNavigate("knowledge")}>Go to Knowledge</button>
            )}
          </section>
        )}

        <section className="overview-destinations">
          <header className="overview-destinations-head">
            <h3>Continue</h3>
            <p>{role === "user" ? "Everyday operator paths" : "Workspace tools"}</p>
          </header>
          <div className="overview-dest-grid">
            <button type="button" className="overview-dest" onClick={() => onNavigate("ask")}>
              <span className="overview-dest-label">Ask</span>
              <strong>Test a guest message</strong>
              <span>Same analysis path the Chrome extension uses.</span>
            </button>
            <button type="button" className="overview-dest" onClick={() => onNavigate("knowledge")}>
              <span className="overview-dest-label">Knowledge</span>
              <strong>{role === "admin" ? "Curate the FAQ" : "Browse curated answers"}</strong>
              <span>
                {role === "admin"
                  ? "Import, export, and finish semantic vectors."
                  : "Read property-scoped guest answers."}
              </span>
            </button>
            {role === "admin" && (
              <button type="button" className="overview-dest" onClick={() => onNavigate("users")}>
                <span className="overview-dest-label">Users</span>
                <strong>Invite operators</strong>
                <span>Cognito logins for the admin app.</span>
              </button>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="stack overview-page">
      <section className="hero overview-hero">
        <div className="overview-hero-copy">
          <span className={`pill ${assistantOn ? "pill-live" : "pill-off"}`}>
            {assistantOn ? "ASSISTANT LIVE" : "ASSISTANT OFF"}
          </span>
          <h2>Guest answers, ready for the house.</h2>
          <p>
            Full control plane — knowledge, model routing, users, and analytics.
            Peak routing selects the judge automatically.
          </p>
          <div className="overview-cta-row">
            <button type="button" className="primary" onClick={() => onNavigate("ask")}>Ask a guest question</button>
            <button type="button" className="secondary ghost-on-dark" onClick={() => onNavigate("knowledge")}>
              {faqCount === 0 ? "Import knowledge" : "Browse knowledge"}
            </button>
          </div>
        </div>
        <aside className="hero-stat overview-judge">
          <span>Live judge</span>
          <strong>{judge?.name ?? "Not configured"}</strong>
          <small>
            {peak ? "Peak window" : "Off-peak"}
            {judge?.model ? ` · ${judge.model}` : judge?.label ? ` · ${judge.label}` : ""}
          </small>
        </aside>
      </section>

      <div className="overview-status" aria-label="System status">
        <div className="overview-status-item">
          <span>Knowledge</span>
          <strong>{faqCount.toLocaleString()}</strong>
          <small>{propertyCount ? `${propertyCount} properties` : "Import a knowledge base"}</small>
        </div>
        <div className="overview-status-item">
          <span>Routing</span>
          <strong>{peak ? "Peak" : "Off-peak"}</strong>
          <small>{judge?.activeWindowLabel || judge?.label || "Automatic peak windows"}</small>
        </div>
        <div className="overview-status-item">
          <span>Usage</span>
          <strong>{(data.usage?.analyze ?? 0).toLocaleString()}</strong>
          <small>analyze · embed {data.usage?.embed ?? 0} · judge {data.usage?.llm_judge ?? 0}</small>
        </div>
        <div className="overview-status-item">
          <span>Matching</span>
          <strong>{data.flags.hybridMatchingEnabled !== false ? "Hybrid" : "Simple"}</strong>
          <small>{assistantOn ? "Assistant accepting requests" : "Assistant disabled in Controls"}</small>
        </div>
      </div>

      {faqCount === 0 && (
        <section className="overview-empty">
          <div>
            <h3>Publish curated knowledge</h3>
            <p>Import the Alamo knowledge-base Excel on Knowledge. Until then, guest analysis returns no answers.</p>
          </div>
          <button type="button" className="primary" onClick={() => onNavigate("knowledge")}>Go to Knowledge</button>
        </section>
      )}

      <section className="overview-destinations">
        <header className="overview-destinations-head">
          <h3>Continue</h3>
          <p>Control-plane tools</p>
        </header>
        <div className="overview-dest-grid">
          <button type="button" className="overview-dest" onClick={() => onNavigate("ask")}>
            <span className="overview-dest-label">Ask</span>
            <strong>Test the guest pipeline</strong>
            <span>Same path the Chrome extension uses.</span>
          </button>
          <button type="button" className="overview-dest" onClick={() => onNavigate("knowledge")}>
            <span className="overview-dest-label">Knowledge</span>
            <strong>Curated FAQ by property</strong>
            <span>Import, export, and browse live answers.</span>
          </button>
          <button type="button" className="overview-dest" onClick={() => onNavigate("models")}>
            <span className="overview-dest-label">Models</span>
            <strong>Providers & peak routing</strong>
            <span>DeepSeek off-peak, OpenAI at peak.</span>
          </button>
          <button type="button" className="overview-dest" onClick={() => onNavigate("controls")}>
            <span className="overview-dest-label">Controls</span>
            <strong>Flags & kill switch</strong>
            <span>Pipeline, channels, and recommended posture.</span>
          </button>
          <button type="button" className="overview-dest" onClick={() => onNavigate("analytics")}>
            <span className="overview-dest-label">Analytics</span>
            <strong>Tokens, cost, traces</strong>
            <span>AI vs non-AI usage for assessment.</span>
          </button>
          <button type="button" className="overview-dest" onClick={() => onNavigate("users")}>
            <span className="overview-dest-label">Users</span>
            <strong>Invite & roles</strong>
            <span>Cognito logins and extension connection.</span>
          </button>
        </div>
      </section>
    </div>
  );
}

function KnowledgePage({ data, role, onRefresh }: { data: DashboardData; role: AppRole; onRefresh: () => Promise<void> }) {
  const canWrite = isAdminPlus(role);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [catalog, setCatalog] = useState<FaqCatalog | null>(null);
  const [propertyFilter, setPropertyFilter] = useState("");
  const [search, setSearch] = useState("");
  const [embedStatus, setEmbedStatus] = useState<{
    configured: boolean;
    total: number;
    withVectors: number;
    remaining: number;
  } | null>(null);

  async function loadEmbedStatus() {
    try {
      setEmbedStatus(await api("/api/admin/faq/embeddings"));
    } catch {
      setEmbedStatus(null);
    }
  }

  useEffect(() => {
    void (async () => {
      // Load vector status independently — catalog payload failures must not hide Voyage counts.
      if (canWrite) void loadEmbedStatus();
      try {
        setCatalog(await api<FaqCatalog>("/api/admin/faq"));
      } catch (e) {
        setMessage(e instanceof Error ? e.message : "Failed to load FAQ catalog");
      }
    })();
  }, [data.faq?.versionId, data.faq?.entries, canWrite]);

  async function uploadKb(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-importing the same file
    if (!file) return;
    if (!window.confirm(
      `Replace ALL knowledge with ${file.name}?\n\n` +
      "Every existing FAQ entry and semantic vector is deleted and rebuilt only from this workbook.",
    )) return;
    setBusy(true);
    setMessage(`Replacing knowledge from ${file.name}…`);
    try {
      const result = await uploadKnowledgeBase(file) as {
        stats: {
          curatedEntries: number;
          curatedEnabled?: number;
          curatedDisabled?: number;
          guestRows: number;
          repairsRows: number;
          properties: number;
          manualEntries?: number;
          guestExtras?: number;
        };
      };
      const enabled = result.stats.curatedEnabled ?? result.stats.curatedEntries;
      const disabled = result.stats.curatedDisabled ?? 0;
      const manual = result.stats.manualEntries ?? 0;
      const extras = result.stats.guestExtras ?? 0;
      setMessage(
        `Published stronger FAQ: ${enabled} guest-ready` +
          (manual || extras ? ` (manual ${manual} + guest extras ${extras})` : "") +
          (disabled ? ` (+${disabled} disabled stubs)` : "") +
          ` across ${result.stats.properties} properties. Finishing Voyage vectors…`,
      );
      try {
        const emb = await api<{ withVectors: number; total: number; remaining: number }>("/api/admin/faq/embed-backfill", {
          method: "POST",
          body: JSON.stringify({ all: true }),
        });
        setMessage(
          `Published ${enabled} guest-ready FAQ entries. Vectors ${emb.withVectors}/${emb.total}` +
            `${emb.remaining ? ` (${emb.remaining} remaining — run Complete vectors again)` : " ready"}.`,
        );
      } catch {
        setMessage(`Published ${enabled} guest-ready FAQ entries. Vector backfill incomplete — use Complete vectors.`);
      }
      await loadEmbedStatus();
      await onRefresh();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Knowledge base import failed");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  async function completeVectors() {
    setBusy(true);
    setMessage("Embedding FAQ rows with Voyage…");
    try {
      const emb = await api<{ withVectors: number; total: number; remaining: number; embedded: number }>("/api/admin/faq/embed-backfill", {
        method: "POST",
        body: JSON.stringify({ all: true }),
      });
      setMessage(emb.remaining === 0
        ? `All ${emb.withVectors} FAQ vectors ready.`
        : `Embedded batch — ${emb.withVectors}/${emb.total} ready (${emb.remaining} left). Run again if needed.`);
      await loadEmbedStatus();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Vector backfill failed");
    } finally {
      setBusy(false);
    }
  }

  async function exportFaq() {
    setBusy(true);
    try {
      await downloadAdminFile("/api/admin/faq/export", "alamo-curated-faq.xlsx");
      setMessage("Exported curated FAQ Excel.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplate() {
    setBusy(true);
    try {
      await downloadAdminFile("/api/admin/faq/template", "alamo-knowledge-base-template.xlsx");
      setMessage("Downloaded knowledge-base template.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Template download failed");
    } finally {
      setBusy(false);
    }
  }

  const properties = (catalog?.properties || data.faq?.properties || []).filter((p) => p !== "Seven Mirror Stays");
  const entries = useMemo(() => {
    const list = (catalog?.entries || []).filter((e) => e.enabled !== false);
    const q = search.trim().toLowerCase();
    return list.filter((e) => {
      if (propertyFilter && e.property !== propertyFilter) return false;
      if (!q) return true;
      return `${e.question} ${e.answer} ${e.category} ${e.property}`.toLowerCase().includes(q);
    });
  }, [catalog, propertyFilter, search]);

  return (
    <div className="manual-page">
      <section className="manual-hero">
        <div>
          <div className="eyebrow">Live knowledge</div>
          <h2>Curated FAQ</h2>
          <p>
            {canWrite
              ? "Guest answers come only from this catalog. Import deletes the current FAQ and rebuilds it from the workbook; then finish semantic vectors so Ask can match nuanced questions."
              : "Browse the published guest answers by property. Use Ask to preview the reply before you respond on Airbnb."}
          </p>
        </div>
        <div className="manual-upload-row">
          {canWrite && (
            <label className="manual-upload-cta">
              <input type="file" accept=".xlsx" onChange={uploadKb} disabled={busy} />
              <span className="upload-glyph">↑</span>
              <span><strong>{busy ? "Working…" : "Import knowledge base"}</strong><small>Replaces all FAQ with this workbook</small></span>
            </label>
          )}
          <button type="button" className="manual-upload-cta" onClick={() => void exportFaq()} disabled={busy}>
            <span className="upload-glyph">↓</span>
            <span><strong>Export FAQ</strong><small>Download curated Excel</small></span>
          </button>
          {canWrite && (
            <button type="button" className="manual-upload-cta" onClick={() => void downloadTemplate()} disabled={busy}>
              <span className="upload-glyph">▤</span>
              <span><strong>Download template</strong><small>Blank knowledge-base Excel</small></span>
            </button>
          )}
        </div>
      </section>

      {message && <div className="manual-notice">{message}</div>}

      <section className={`manual-stats ${canWrite ? "manual-stats-admin" : "manual-stats-operator"}`}>
        <div><span>Curated FAQ</span><strong>{catalog?.entries?.filter((e) => e.enabled !== false).length ?? data.faq?.entries ?? 0}</strong></div>
        <div><span>Properties</span><strong>{properties.length}</strong></div>
        <div><span>Categories</span><strong>{catalog?.categories?.length ?? data.faq?.categories?.length ?? 0}</strong></div>
        {canWrite && (
          <div>
            <span>Semantic vectors</span>
            <strong>
              {embedStatus
                ? `${embedStatus.withVectors}/${embedStatus.total}`
                : "—"}
            </strong>
            {embedStatus && embedStatus.remaining > 0 && (
              <button type="button" className="text-button" style={{ marginTop: 6 }} disabled={busy} onClick={() => void completeVectors()}>
                Complete vectors
              </button>
            )}
            {embedStatus && !embedStatus.configured && <small className="muted">Embeddings not configured</small>}
            {embedStatus && embedStatus.configured && embedStatus.remaining === 0 && embedStatus.total > 0 && (
              <small className="muted">Matching ready</small>
            )}
          </div>
        )}
      </section>

      <section className="panel">
        <PanelHeader
          title="Browse FAQ"
          subtitle={propertyFilter ? `Filtered to ${propertyFilter} · ${entries.length} entries` : `All properties · ${entries.length} entries`}
        />
        <div className="faq-toolbar">
          <input
            className="faq-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search question, answer, or category…"
          />
          {propertyFilter && (
            <button type="button" className="secondary" onClick={() => setPropertyFilter("")}>Clear property filter</button>
          )}
        </div>
        {properties.length === 0 ? (
          <div className="manual-empty"><strong>No properties yet</strong><p>{canWrite ? "Import the Alamo knowledge base to publish curated FAQ." : "Ask an admin to import the knowledge base."}</p></div>
        ) : (
          <div className="property-chips">
            {properties.map((name) => (
              <button
                key={name}
                type="button"
                className={propertyFilter === name ? "chip filter selected" : "chip filter"}
                onClick={() => setPropertyFilter((prev) => (prev === name ? "" : name))}
              >
                {name}
              </button>
            ))}
          </div>
        )}
        <div className="faq-entry-list">
          {entries.length === 0 ? (
            <div className="manual-empty"><strong>No matching entries</strong><p>Try another property or search term.</p></div>
          ) : entries.slice(0, 80).map((entry: FaqEntry) => (
            <article key={entry.id} className="faq-entry">
              <div className="faq-entry-meta">
                <span className="chip">{entry.property}</span>
                <span className="muted">{entry.category}</span>
              </div>
              <strong>{entry.question}</strong>
              <p>{entry.answer}</p>
            </article>
          ))}
          {entries.length > 80 && <div className="inline-message">Showing first 80 of {entries.length}. Narrow with search or a property filter.</div>}
        </div>
      </section>
    </div>
  );
}

const DEFAULT_ROUTING: JudgeRouting = {
  autoPeakRouting: true,
  primaryProfileId: "deepseek-direct",
  peakProfileId: "openai-gpt4o-mini",
  weekdaysOnly: true,
  peakWindows: [
    { label: "Morning (Beijing 09:00-12:00)", startUtc: "01:00", endUtc: "04:00" },
    { label: "Afternoon (Beijing 14:00-18:00)", startUtc: "06:00", endUtc: "10:00" },
  ],
};

const EMPTY_MODEL = {
  id: "",
  name: "",
  provider: "openai-compatible" as LlmProfile["provider"],
  baseUrl: "https://api.openai.com/v1",
  model: "",
  apiKey: "",
  enabled: true,
  isNew: true,
};

function slugId(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 48) || `model-${Date.now()}`;
}

function LlmManager({ data, onRefresh }: { data: DashboardData; onRefresh: () => Promise<void> }) {
  const models = data.llm.profiles;
  const [routing, setRouting] = useState<JudgeRouting>(() => ({
    ...DEFAULT_ROUTING,
    ...(data.settings.judgeRouting ?? {}),
    peakWindows: data.settings.judgeRouting?.peakWindows?.length
      ? data.settings.judgeRouting.peakWindows
      : DEFAULT_ROUTING.peakWindows,
  }));
  const [draft, setDraft] = useState({ ...EMPTY_MODEL });
  const [selectedId, setSelectedId] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const selectSeqRef = useMemo(() => ({ current: 0 }), []);

  useEffect(() => {
    setRouting({
      ...DEFAULT_ROUTING,
      ...(data.settings.judgeRouting ?? {}),
      peakWindows: data.settings.judgeRouting?.peakWindows?.length
        ? data.settings.judgeRouting.peakWindows
        : DEFAULT_ROUTING.peakWindows,
    });
  }, [data.settings.judgeRouting]);

  const profileName = (id: string) => models.find((m) => m.id === id)?.name ?? id;

  const nowPreview = useMemo(() => {
    const parse = (hm: string) => {
      const [h, m] = hm.split(":").map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    const inWindow = (w: PeakWindowUtc, cur: number) => {
      const start = parse(w.startUtc);
      const end = parse(w.endUtc);
      if (start === end) return true;
      if (start < end) return cur >= start && cur < end;
      return cur >= start || cur < end;
    };
    const d = new Date();
    const cur = d.getUTCHours() * 60 + d.getUTCMinutes();
    const weekday = d.getUTCDay() !== 0 && d.getUTCDay() !== 6;
    if (!routing.autoPeakRouting) {
      return { label: `Fixed — ${profileName(routing.primaryProfileId)}`, profileId: routing.primaryProfileId, window: "" };
    }
    if (routing.weekdaysOnly !== false && !weekday) {
      return { label: `Weekend off-peak — ${profileName(routing.primaryProfileId)}`, profileId: routing.primaryProfileId, window: "" };
    }
    const hit = routing.peakWindows.find((w) => inWindow(w, cur));
    if (hit) {
      return {
        label: `Peak — ${profileName(routing.peakProfileId)}`,
        profileId: routing.peakProfileId,
        window: hit.label || `${hit.startUtc}-${hit.endUtc} UTC`,
      };
    }
    return { label: `Off-peak — ${profileName(routing.primaryProfileId)}`, profileId: routing.primaryProfileId, window: "" };
  }, [routing, models]);

  async function selectModel(p: LlmProfile) {
    const seq = ++selectSeqRef.current;
    setSelectedId(p.id);
    setShowKey(false);
    setDraft({
      id: p.id,
      name: p.name,
      provider: p.provider,
      baseUrl: p.baseUrl ?? "",
      model: p.model ?? "",
      apiKey: "",
      enabled: p.enabled,
      isNew: false,
    });
    setStatus(`Loading ${p.name}…`);
    setBusy(true);
    try {
      const full = await api<LlmProfile>(`/api/admin/llm/profiles/${encodeURIComponent(p.id)}`);
      if (seq !== selectSeqRef.current) return;
      setDraft({
        id: full.id,
        name: full.name,
        provider: full.provider,
        baseUrl: full.baseUrl ?? "",
        model: full.model ?? "",
        // Never prefill the secret — omit empty on save keeps the stored/SSM key.
        apiKey: "",
        enabled: full.enabled,
        isNew: false,
      });
      const keyNote = full.apiKeyConfigured
        ? full.apiKeyHint
          ? ` · key ${full.apiKeyHint}`
          : " · key configured"
        : " · no key";
      setStatus(`Editing ${full.name}${keyNote}`);
    } catch (e) {
      if (seq !== selectSeqRef.current) return;
      setStatus(e instanceof Error ? e.message : "Load failed — card details shown without key");
    } finally {
      if (seq === selectSeqRef.current) setBusy(false);
    }
  }

  useEffect(() => {
    if (selectedId || draft.isNew && draft.name === "New model") return;
    const preferredId = data.resolvedJudge?.profileId;
    const preferred = models.find((m) => m.id === preferredId)
      || models.find((m) => m.enabled !== false && m.provider !== "mock")
      || models[0];
    if (preferred) void selectModel(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.resolvedJudge?.profileId, models.map((m) => m.id).join("|")]);

  function startNew() {
    selectSeqRef.current += 1;
    setSelectedId("");
    setDraft({ ...EMPTY_MODEL, id: "", name: "New model" });
    setShowKey(true);
    setStatus("Register a new OpenAI-compatible model (any vendor).");
  }

  async function saveModel() {
    setBusy(true);
    try {
      const id = draft.isNew ? (draft.id.trim() || slugId(draft.name)) : draft.id;
      const body = {
        name: draft.name.trim() || id,
        provider: draft.provider,
        baseUrl: draft.baseUrl.trim(),
        model: draft.model.trim(),
        enabled: draft.enabled,
        ...(draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
      };
      const saved = await api<LlmProfile>(`/api/admin/llm/profiles/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setSelectedId(saved.id);
      setDraft({
        id: saved.id,
        name: saved.name,
        provider: saved.provider,
        baseUrl: saved.baseUrl ?? "",
        model: saved.model ?? "",
        apiKey: "",
        enabled: saved.enabled,
        isNew: false,
      });
      const keyNote = saved.apiKeyConfigured
        ? saved.apiKeyHint
          ? ` · key ${saved.apiKeyHint}`
          : " · key configured"
        : "";
      setStatus(`Saved ${saved.name}${keyNote}.`);
      await onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function testModel() {
    setBusy(true);
    setStatus("Testing…");
    try {
      const r = await api<{ ok: boolean; latencyMs: number; model: string; error?: string }>("/api/admin/llm/test", {
        method: "POST",
        body: JSON.stringify({
          id: draft.id || slugId(draft.name),
          name: draft.name,
          provider: draft.provider,
          baseUrl: draft.baseUrl,
          model: draft.model,
          ...(draft.apiKey.trim() ? { apiKey: draft.apiKey.trim() } : {}),
        }),
      });
      setStatus(r.ok ? `Connection OK · ${r.latencyMs} ms · ${r.model}` : `Failed · ${r.error ?? "error"}`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Test failed");
    } finally {
      setBusy(false);
    }
  }

  async function deleteModel() {
    if (!draft.id || draft.isNew) return;
    if (draft.id === "default-mock") { setStatus("Cannot delete the mock profile."); return; }
    if (!confirm(`Delete model "${draft.name}"?`)) return;
    setBusy(true);
    try {
      await api(`/api/admin/llm/profiles/${encodeURIComponent(draft.id)}`, { method: "DELETE" });
      setSelectedId("");
      setDraft({ ...EMPTY_MODEL });
      setStatus("Deleted.");
      await onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveRouting() {
    setBusy(true);
    try {
      const cleanWindows = routing.peakWindows
        .filter((w) => w.startUtc.trim() && w.endUtc.trim())
        .map((w) => ({ label: w.label?.trim() || undefined, startUtc: w.startUtc.trim(), endUtc: w.endUtc.trim() }));
      if (!cleanWindows.length) throw new Error("Add at least one peak window (or turn off auto-switch).");
      await api("/api/admin/settings", {
        method: "PUT",
        body: JSON.stringify({ judgeRouting: { ...routing, peakWindows: cleanWindows } }),
      });
      setStatus("Routing saved.");
      await onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Routing save failed");
    } finally {
      setBusy(false);
    }
  }

  function addWindow() {
    setRouting((r) => ({
      ...r,
      peakWindows: [...r.peakWindows, { label: `Window ${r.peakWindows.length + 1}`, startUtc: "00:00", endUtc: "01:00" }],
    }));
  }

  function removeWindow(index: number) {
    setRouting((r) => ({ ...r, peakWindows: r.peakWindows.filter((_, i) => i !== index) }));
  }

  function updateWindow(index: number, patch: Partial<PeakWindowUtc>) {
    setRouting((r) => ({
      ...r,
      peakWindows: r.peakWindows.map((w, i) => (i === index ? { ...w, ...patch } : w)),
    }));
  }

  const live = data.resolvedJudge;

  return (
    <div className="stack">
      <section className="panel live-now">
        <PanelHeader title="Live now" subtitle="Resolved judge from peak routing (what guests get)" />
        <div className="live-now-grid">
          <div><span>Server</span><strong>{live?.name ?? "—"}</strong><small>{live?.label ?? ""}{live?.model ? ` · ${live.model}` : ""}</small></div>
          <div><span>Editor preview</span><strong>{profileName(nowPreview.profileId)}</strong><small>{nowPreview.label}{nowPreview.window ? ` · ${nowPreview.window}` : ""}</small></div>
        </div>
      </section>

      <section className="panel">
        <PanelHeader
          title="Model registry"
          subtitle="Register any OpenAI-compatible chat model. Keys are stored encrypted."
        />
        <div className="profile-strip" style={{ flexWrap: "wrap" }}>
          {models.map((p) => (
            <button key={p.id} className={p.id === selectedId ? "profile active" : "profile"} onClick={() => void selectModel(p)} disabled={busy}>
              <strong>{p.name}</strong>
              <span>{p.model || p.provider} · {p.apiKeyConfigured ? "Key set" : "No key"}</span>
            </button>
          ))}
        </div>

        <div className="form-grid" style={{ marginTop: 16 }}>
          <Field label="Display name">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, id: draft.isNew ? slugId(e.target.value) : draft.id })} />
          </Field>
          <Field label="Profile ID (stable)">
            <input value={draft.id} disabled={!draft.isNew} onChange={(e) => setDraft({ ...draft, id: e.target.value.trim() })} placeholder="auto from name" />
          </Field>
          <Field label="Provider type">
            <select value={draft.provider} onChange={(e) => setDraft({ ...draft, provider: e.target.value as LlmProfile["provider"] })}>
              <option value="openai-compatible">OpenAI-compatible (generic)</option>
              <option value="mock">Mock (no network)</option>
            </select>
          </Field>
          <Field label="Base URL">
            <input value={draft.baseUrl} disabled={draft.provider === "mock"} onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })} placeholder="https://api.deepseek.com" />
          </Field>
          <Field label="Model id">
            <input value={draft.model} disabled={draft.provider === "mock"} onChange={(e) => setDraft({ ...draft, model: e.target.value })} placeholder="deepseek-chat / gpt-4o-mini / …" />
          </Field>
          <Field label="API key">
            <div style={{ display: "flex", gap: 8 }}>
              <input
                style={{ flex: 1 }}
                type={showKey ? "text" : "password"}
                value={draft.apiKey}
                onChange={(e) => setDraft({ ...draft, apiKey: e.target.value })}
                placeholder={
                  draft.isNew
                    ? "Paste API key"
                    : models.find((m) => m.id === draft.id)?.apiKeyConfigured
                      ? "Leave blank to keep current key"
                      : "Paste API key (or set via SSM)"
                }
                autoComplete="off"
              />
              <button type="button" className="secondary" onClick={() => setShowKey((s) => !s)}>{showKey ? "Hide" : "Show"}</button>
            </div>
          </Field>
        </div>
        <label className="check-row" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
          <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
          <span>Enabled for routing</span>
        </label>
        <div className="button-row" style={{ marginTop: 12 }}>
          <button className="primary" disabled={busy || !draft.name} onClick={() => void saveModel()}>Save model</button>
          <button className="secondary" disabled={busy || !draft.baseUrl} onClick={() => void testModel()}>Test connection</button>
        </div>
      </section>

      <section className="panel">
        <PanelHeader title="Peak routing" subtitle="Pick registered models for off-peak vs peak. Add UTC windows as needed." />
        <label className="check-row" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          <input type="checkbox" checked={routing.autoPeakRouting} onChange={(e) => setRouting({ ...routing, autoPeakRouting: e.target.checked })} />
          <span>Auto-switch by peak windows</span>
        </label>
        <label className="check-row" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
          <input type="checkbox" checked={routing.weekdaysOnly !== false} onChange={(e) => setRouting({ ...routing, weekdaysOnly: e.target.checked })} />
          <span>Weekdays only (weekends always off-peak)</span>
        </label>
        <div className="form-grid" style={{ marginBottom: 16 }}>
          <Field label="Off-peak model">
            <select value={routing.primaryProfileId} onChange={(e) => setRouting({ ...routing, primaryProfileId: e.target.value })}>
              {models.filter((m) => m.enabled !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Peak model">
            <select value={routing.peakProfileId} onChange={(e) => setRouting({ ...routing, peakProfileId: e.target.value })}>
              {models.filter((m) => m.enabled !== false).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </Field>
        </div>

        {routing.peakWindows.map((w, index) => (
          <div key={index} className="panel" style={{ marginBottom: 10, background: "#fafbfc" }}>
            <div className="button-row" style={{ marginBottom: 8 }}>
              <strong>Window {index + 1}</strong>
              <button type="button" className="text-button deactivate" disabled={busy} onClick={() => removeWindow(index)}>Remove</button>
            </div>
            <div className="form-grid">
              <Field label="Label"><input value={w.label ?? ""} onChange={(e) => updateWindow(index, { label: e.target.value })} /></Field>
              <Field label="Start UTC (HH:MM)"><input value={w.startUtc} onChange={(e) => updateWindow(index, { startUtc: e.target.value })} /></Field>
              <Field label="End UTC (HH:MM)"><input value={w.endUtc} onChange={(e) => updateWindow(index, { endUtc: e.target.value })} /></Field>
            </div>
          </div>
        ))}

        <div className="button-row">
          <button type="button" className="secondary" disabled={busy} onClick={addWindow}>+ Add peak window</button>
          <button type="button" className="secondary" disabled={busy} onClick={() => setRouting({ ...DEFAULT_ROUTING })}>Reset DeepSeek defaults</button>
          <button type="button" className="primary" disabled={busy} onClick={() => void saveRouting()}>Save routing</button>
        </div>
      </section>

      {status && <div className="inline-message">{status}</div>}
    </div>
  );
}

type StayInfo = { propertyName?: string; stayDates?: string; stayStatus?: string };

const STAY_STATUS_LABEL: Record<string, string> = {
  currently_hosting: "Currently hosting",
  upcoming: "Upcoming",
  past: "Past",
  expired: "Expired invite",
};

function stayStatusLabel(status: string): string {
  return STAY_STATUS_LABEL[status] ?? status.replace(/_/g, " ");
}

/** Always-visible Property / Stay status / Stay dates row ("—" when not captured). */
function StayFields({ propertyName, stayDates, stayStatus }: StayInfo) {
  const item = (label: string, value?: string) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="label" style={{ margin: 0 }}>{label}</span>
      <span className={value ? "" : "muted"}>{value || "—"}</span>
    </div>
  );
  return (
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
      {item("Property", propertyName)}
      {item("Stay status", stayStatus ? stayStatusLabel(stayStatus) : undefined)}
      {item("Stay dates", stayDates)}
    </div>
  );
}

/** Status · Property · Dates chips shared by Ask results and Analytics traces. */
function StayChips({ propertyName, stayDates, stayStatus, compact }: StayInfo & { compact?: boolean }) {
  if (!propertyName && !stayDates && !stayStatus) return null;
  const size = compact ? "0.7rem" : "0.82rem";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? 4 : 6, flexWrap: "wrap", marginTop: compact ? 4 : 0, marginBottom: compact ? 0 : 10 }}>
      {stayStatus && (
        <span className="chip" style={{ fontSize: compact ? size : undefined }}>
          {stayStatusLabel(stayStatus)}
        </span>
      )}
      {propertyName && <span className="chip" style={{ fontSize: compact ? size : undefined }}>{propertyName}</span>}
      {stayDates && <span className="muted" style={{ fontSize: size }}>{compact ? stayDates : `· ${stayDates}`}</span>}
    </div>
  );
}

function Playground({ data, role, onRefresh }: { data: DashboardData; role: AppRole; onRefresh: () => Promise<void> }) {
  const isSuper = role === "super-admin";
  const [query, setQuery] = useState("What time is checkout?");
  const [propertyName, setPropertyName] = useState("");
  const [stayDates, setStayDates] = useState("");
  const [stayStatus, setStayStatus] = useState("");
  const [result, setResult] = useState<AnalysisResult | null>(null);
  // Stay context the shown result was produced with (not the live form values).
  const [resultStay, setResultStay] = useState<StayInfo>({});
  const [judge, setJudge] = useState<{ profileId: string; reason: string; inPeakWindow: boolean; name?: string } | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    setStatus("");
    const sent: StayInfo = {
      propertyName: propertyName.trim() || undefined,
      stayDates: stayDates.trim() || undefined,
      stayStatus: stayStatus || undefined,
    };
    try {
      const r = await api<{
        result: AnalysisResult;
        profile: { id: string; name: string } | null;
        judge: { profileId: string; reason: string; inPeakWindow: boolean } | null;
      }>("/api/admin/playground", {
        method: "POST",
        body: JSON.stringify({ query, ...sent }),
      });
      setResult(r.result);
      setResultStay(sent);
      setJudge(isSuper && r.judge ? { ...r.judge, name: r.profile?.name } : null);
      setStatus("Analysis complete.");
      void onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Analysis failed");
    } finally {
      setBusy(false);
    }
  }

  const propertyOptions = (data.faq?.properties || []).filter((p) => p !== "Seven Mirror Stays");

  return (
    <div className="grid-2">
      <section className="panel">
        <PanelHeader title="Ask like a guest" subtitle="Same analysis pipeline used by the extension." />
        <div className="form-grid compact" style={{ marginBottom: 12 }}>
          <Field label="Property scope">
            <select value={propertyName} onChange={(e) => setPropertyName(e.target.value)}>
              <option value="">All properties</option>
              {propertyOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Stay dates (optional)">
            <input
              type="text"
              value={stayDates}
              onChange={(e) => setStayDates(e.target.value)}
              placeholder="e.g. 27 Dec 2026 – 4 Jan 2027"
            />
          </Field>
          <Field label="Stay status (optional)">
            <select value={stayStatus} onChange={(e) => setStayStatus(e.target.value)}>
              <option value="">— not specified —</option>
              <option value="upcoming">Upcoming</option>
              <option value="currently_hosting">Currently hosting</option>
              <option value="past">Past</option>
              <option value="expired">Expired invite</option>
            </select>
          </Field>
        </div>
        <label className="field">
          <span>Guest message</span>
          <textarea rows={7} value={query} onChange={(e) => setQuery(e.target.value)} />
          <small>
            Assistant {data.flags.assistantEnabled ? "ON" : "OFF"}
            {isSuper ? ` · LLM judge ${data.flags.llmMatchingEnabled ? "ON" : "OFF"}` : ""}
            {propertyName ? ` · ${propertyName}` : " · unscoped"}
          </small>
        </label>
        <div className="button-row">
          <button className="primary" disabled={busy || !query.trim()} onClick={() => void run()}>
            {busy ? "Analyzing…" : "Analyze message"}
          </button>
          {result && (
            <button className="secondary" type="button" disabled={busy} onClick={() => { setResult(null); setJudge(null); setStatus(""); }}>
              Clear result
            </button>
          )}
        </div>
        {status && <div className="inline-message">{status}</div>}
      </section>
      {result ? (
        <section className={`panel result ${result.band}`}>
          {/* Status · Property · Dates — mirrors what the Chrome extension shows */}
          <StayFields {...resultStay} />
          <div className="result-head">
            <span className={`badge ${result.band === "green" ? "success" : "warning"}`}>{result.band.toUpperCase()}</span>
            <strong>{result.similarity}%</strong>
          </div>
          {isSuper && judge && (
            <p className="muted" style={{ marginTop: 0 }}>
              Judge: {judge.name || judge.profileId} · {judge.inPeakWindow ? "peak" : judge.reason}
            </p>
          )}
          {result.bestEntry && (
            <>
              <div className="label">Matched knowledge</div>
              <p>
                <strong>{result.bestEntry.question}</strong>
                <br />
                <span className="muted">{result.sourceSheet}</span>
              </p>
            </>
          )}
          <div className="label">Suggested reply</div>
          <div className="reply">{result.answer || "No answer found."}</div>
        </section>
      ) : (
        <section className="panel empty ask-empty">
          <span className="big-icon">◇</span>
          <strong>Run a conversation test</strong>
          <p>
            {isSuper
              ? "Preview the guest-ready reply, confidence, and which model judged the match."
              : "Preview the guest-ready reply and confidence before you answer on Airbnb."}
          </p>
        </section>
      )}
    </div>
  );
}

function ControlsPage({ flags, onRefresh }: { flags: Flags; onRefresh: () => Promise<void> }) {
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  return (
    <div className="stack">
      <section className="panel">
        <PanelHeader
          title="Role access"
          subtitle="Who can see what in this admin app. Extension hosts use the Analyze API key — not these Cognito roles."
        />
        <div className="role-matrix">
          <div className="role-matrix-row">
            <strong>user</strong>
            <span>Overview + Ask (playground). Browse Knowledge read-only. No Models / Controls / Users / Analytics.</span>
          </div>
          <div className="role-matrix-row">
            <strong>admin</strong>
            <span>Everything user has, plus Knowledge import and Users invite/role (cannot grant super-admin). No Models / Controls / Analytics.</span>
          </div>
          <div className="role-matrix-row">
            <strong>super-admin</strong>
            <span>Full access: Models, Controls, Analytics, and promoting other super-admins.</span>
          </div>
        </div>
      </section>
      <FlagsManager flags={flags} onRefresh={onRefresh} />
      <section className="panel">
        <PanelHeader
          title="Diagnostics"
          subtitle="Low-level API tester — super-admin only"
          action={<button className="secondary" type="button" onClick={() => setShowDiagnostics((v) => !v)}>{showDiagnostics ? "Hide" : "Show"}</button>}
        />
        {showDiagnostics && <ApiTester />}
      </section>
    </div>
  );
}

function FlagsManager({ flags, onRefresh }: { flags: Flags; onRefresh: () => Promise<void> }) {
  const [local, setLocal] = useState(flags);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setLocal(flags), [flags]);

  async function toggle(key: keyof Flags) {
    const next = { ...local, [key]: !local[key] };
    setLocal(next);
    try {
      await api<Flags>("/api/admin/flags", { method: "PUT", body: JSON.stringify({ [key]: next[key] }) });
      setStatus(`${FLAG_META[key].label} ${next[key] ? "enabled" : "disabled"}.`);
      await onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Update failed");
      setLocal(flags);
    }
  }

  async function applyRecommended() {
    setBusy(true);
    try {
      const patch = Object.fromEntries(
        (Object.keys(FLAG_META) as Array<keyof Flags>).map((k) => [k, FLAG_META[k].recommended]),
      ) as unknown as Flags;
      const updated = await api<Flags>("/api/admin/flags", { method: "PUT", body: JSON.stringify(patch) });
      setLocal(updated);
      setStatus("Applied Alamo recommended Controls (Teams OFF, debug OFF, core pipeline ON).");
      await onRefresh();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed to apply recommended flags");
    } finally {
      setBusy(false);
    }
  }

  const groups: Array<{ id: "core" | "channels" | "ops"; title: string; subtitle: string }> = [
    { id: "core", title: "Matching pipeline", subtitle: "What Ask /analyze actually uses today (Hybrid A → Voyage → LLM judge)." },
    { id: "channels", title: "Extension channels", subtitle: "Where the Chrome extension is allowed to read guest messages." },
    { id: "ops", title: "Operations", subtitle: "Telemetry and diagnostics — not part of the guest reply path." },
  ];

  return (
    <section className="panel">
      <PanelHeader
        title="Runtime controls"
        subtitle="Apply immediately to hosted /analyze and the extension. Recommended posture is Alamo Airbnb + hybrid + LLM judge."
        action={
          <button className="secondary" type="button" disabled={busy} onClick={() => void applyRecommended()}>
            {busy ? "Applying…" : "Apply recommended"}
          </button>
        }
      />
      {groups.map((group) => {
        const keys = (Object.keys(local) as Array<keyof Flags>).filter((k) => FLAG_META[k].group === group.id);
        return (
          <div key={group.id} className="flag-group">
            <div className="flag-group-head">
              <strong>{group.title}</strong>
              <span>{group.subtitle}</span>
            </div>
            <div className="flag-list">
              {keys.map((key) => {
                const meta = FLAG_META[key];
                const offRecommended = local[key] !== meta.recommended;
                return (
                  <div className="flag-row" key={key}>
                    <div>
                      <strong>
                        {meta.label}
                        {offRecommended && <span className="flag-rec-pill">differs from recommended</span>}
                      </strong>
                      <span>{meta.blurb}</span>
                    </div>
                    <button className={local[key] ? "switch on" : "switch"} onClick={() => void toggle(key)} type="button">
                      <span />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {status && <div className="inline-message">{status}</div>}
    </section>
  );
}

function ApiTester() {
  const [method, setMethod] = useState("GET");
  const [path, setPath] = useState("/health");
  const [body, setBody] = useState("");
  const [response, setResponse] = useState("");
  const [busy, setBusy] = useState(false);
  const examples = [
    ["GET", "/health", ""] as const,
    ["GET", "/api/admin/me", ""] as const,
    ["GET", "/api/runtime-config", ""] as const,
    ["POST", "/analyze", JSON.stringify({ query: "What time is checkout?", source: "airbnb" }, null, 2)] as const,
  ];
  async function run() {
    setBusy(true);
    try {
      const headers = new Headers(authHeaders(body ? { "Content-Type": "application/json" } : {}));
      const r = await fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body: body || undefined,
      });
      const text = await r.text();
      setResponse(`${r.status} ${r.statusText}\n\n${text}`);
    } catch (e) {
      setResponse(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ marginTop: 8 }}>
      <div className="example-row">
        {examples.map(([m, p, b]) => (
          <button key={p} className="chip" onClick={() => { setMethod(m); setPath(p); setBody(b); }}>{m} {p}</button>
        ))}
      </div>
      <div className="form-grid compact">
        <Field label="Method">
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option>
          </select>
        </Field>
        <Field label="Endpoint path"><input value={path} onChange={(e) => setPath(e.target.value)} /></Field>
      </div>
      <label className="field">
        <span>JSON body</span>
        <textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
      </label>
      <button className="primary" disabled={busy} onClick={() => void run()}>{busy ? "Sending…" : "Send request"}</button>
      <pre className="codeblock">{response || "No request sent yet."}</pre>
    </div>
  );
}

function UsersPage({ meRole }: { meRole: AppRole }) {
  const [users, setUsers] = useState<ControlUser[]>([]);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AppRole>("user");
  const [invitePassword, setInvitePassword] = useState<string | null>(null);
  const [draftRoles, setDraftRoles] = useState<Record<string, AppRole>>({});
  const [extensionInfo, setExtensionInfo] = useState<{
    backendUrl: string;
    analyzeApiKeyConfigured: boolean;
    endpoints: string[];
    headerHint: string;
    note: string;
  } | null>(null);
  const canPromoteSuper = meRole === "super-admin";

  async function load() {
    setBusy(true);
    try {
      const [payload, ext] = await Promise.all([
        api<{ users: ControlUser[] }>("/api/admin/users"),
        api<{
          backendUrl: string;
          analyzeApiKeyConfigured: boolean;
          endpoints: string[];
          headerHint: string;
          note: string;
        }>("/api/admin/extension-connection"),
      ]);
      setUsers(payload.users);
      setDraftRoles(Object.fromEntries(payload.users.map((u) => [u.email, u.role])));
      setExtensionInfo(ext);
      setStatus("");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function saveRole(email: string) {
    const role = draftRoles[email];
    if (!role) return;
    setBusy(true);
    try {
      await api(`/api/admin/users/${encodeURIComponent(email)}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      });
      setStatus(`Updated ${email} → ${role}`);
      await load();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Role update failed");
    } finally {
      setBusy(false);
    }
  }

  async function inviteUser(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setInvitePassword(null);
    try {
      const result = await api<{ user: ControlUser; temporaryPassword: string }>("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setInvitePassword(result.temporaryPassword);
      setStatus(`Invited ${result.user.email} as ${result.user.role}. Share the temporary password once.`);
      setInviteEmail("");
      setInviteRole("user");
      await load();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Invite failed");
    } finally {
      setBusy(false);
    }
  }

  const roleOptions: AppRole[] = canPromoteSuper
    ? ["super-admin", "admin", "user"]
    : ["admin", "user"];

  const adminOrigin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div className="stack">
      <section className="panel">
        <PanelHeader
          title="Invite user"
          subtitle="Creates a Cognito login for the admin app. Not used by the Chrome extension."
        />
        <form className="form-grid" onSubmit={(e) => void inviteUser(e)}>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="name@company.com"
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Role</span>
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as AppRole)}>
              {roleOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          <div className="button-row" style={{ gridColumn: "1 / -1" }}>
            <button className="primary" type="submit" disabled={busy || !inviteEmail.trim()}>
              {busy ? "Working…" : "Invite user"}
            </button>
          </div>
        </form>
        {invitePassword && (
          <div className="inline-message" style={{ marginTop: 12 }}>
            Temporary password (copy now — shown once): <code>{invitePassword}</code>
          </div>
        )}
      </section>

      <section className="panel">
        <PanelHeader
          title="Control-plane users"
          subtitle="user = Ask; admin = Knowledge + Users; super-admin = Models / Controls / Analytics. Extension hosts do not use these roles."
          action={<button className="secondary" disabled={busy} onClick={() => void load()}>Refresh</button>}
        />
        {status && <div className="inline-message">{status}</div>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Email</th><th>Status</th><th>Role</th></tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const draft = draftRoles[user.email] ?? user.role;
                const dirty = draft !== user.role;
                return (
                  <tr key={user.email}>
                    <td>
                      <strong>{user.email}</strong>
                      <div className="muted">{user.groups.join(", ") || "—"}</div>
                    </td>
                    <td>{user.status}{user.enabled ? "" : " (disabled)"}</td>
                    <td>
                      <div className="role-edit-row">
                        <select
                          className="role-select"
                          value={draft}
                          disabled={busy || (user.role === "super-admin" && !canPromoteSuper)}
                          onChange={(e) => setDraftRoles((prev) => ({ ...prev, [user.email]: e.target.value as AppRole }))}
                        >
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                          {!roleOptions.includes(user.role) && (
                            <option value={user.role}>{user.role}</option>
                          )}
                        </select>
                        <button
                          className="secondary"
                          type="button"
                          disabled={busy || !dirty}
                          onClick={() => void saveRole(user.email)}
                        >
                          Save
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {users.length === 0 && !busy && (
            <div className="empty">No Cognito users yet. Invite someone above.</div>
          )}
        </div>
      </section>

      <section className="panel">
        <PanelHeader
          title="Chrome extension connection"
          subtitle="Shared Analyze API key — not a Cognito user on this list."
        />
        {extensionInfo ? (
          <div className="extension-conn">
            <div className="field">
              <span>Backend URL for extension Options</span>
              <code className="code-inline">{adminOrigin || extensionInfo.backendUrl}</code>
            </div>
            <div className="field">
              <span>Analyze API key</span>
              <strong className={extensionInfo.analyzeApiKeyConfigured ? "text-success" : ""}>
                {extensionInfo.analyzeApiKeyConfigured ? "Configured on server" : "Missing on server"}
              </strong>
              <small>Paste the same key from deploy secrets into the extension Options page. It is never shown here.</small>
            </div>
            <div className="field">
              <span>Allowed APIs</span>
              <small>{extensionInfo.endpoints.join(" · ")}</small>
              <small>{extensionInfo.headerHint}</small>
            </div>
            <p className="muted">{extensionInfo.note}</p>
          </div>
        ) : (
          <div className="empty">Loading connection info…</div>
        )}
      </section>
    </div>
  );
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function defaultAnalyticsRange(days = 14): { from: string; to: string } {
  const to = new Date();
  const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return { from: isoDay(from), to: isoDay(to) };
}

function AnalyticsPage() {
  const initialRange = defaultAnalyticsRange(14);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [traces, setTraces] = useState<AnalyticsTrace[]>([]);
  const [rates, setRates] = useState<ModelRate[]>([]);
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [appliedFrom, setAppliedFrom] = useState(initialRange.from);
  const [appliedTo, setAppliedTo] = useState(initialRange.to);
  const [activePreset, setActivePreset] = useState<7 | 14 | 30 | null>(14);
  const [usedAiFilter, setUsedAiFilter] = useState<"all" | "ai" | "non-ai">("all");
  const [search, setSearch] = useState("");
  const [hoverDay, setHoverDay] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  async function load(opts?: {
    from?: string;
    to?: string;
    usedAi?: "all" | "ai" | "non-ai";
    q?: string;
  }) {
    const from = opts?.from ?? appliedFrom;
    const to = opts?.to ?? appliedTo;
    const filter = opts?.usedAi ?? usedAiFilter;
    const q = opts?.q ?? search;
    if (from > to) {
      setStatus("From date must be on or before To date.");
      return;
    }
    setBusy(true);
    try {
      const range = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const usedAiParam = filter === "ai" ? "&usedAi=true" : filter === "non-ai" ? "&usedAi=false" : "";
      const qParam = q.trim() ? `&q=${encodeURIComponent(q.trim())}` : "";
      const [s, t, p] = await Promise.all([
        api<AnalyticsSummary>(`/api/admin/analytics/summary?${range}`),
        api<{ traces: AnalyticsTrace[] }>(`/api/admin/analytics/traces?limit=80&${range}${usedAiParam}${qParam}`),
        api<{ rates: ModelRate[] }>("/api/admin/analytics/pricing"),
      ]);
      setSummary(s);
      setTraces(t.traces);
      setRates(p.rates);
      setAppliedFrom(from);
      setAppliedTo(to);
      setStatus("");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed to load analytics");
    } finally {
      setBusy(false);
    }
  }

  function runSearch() {
    setActivePreset(null);
    void load({ from: fromDate, to: toDate });
  }

  function applyPreset(days: 7 | 14 | 30) {
    const range = defaultAnalyticsRange(days);
    setFromDate(range.from);
    setToDate(range.to);
    setActivePreset(days);
    void load({ from: range.from, to: range.to });
  }

  function setRouteFilter(next: "all" | "ai" | "non-ai") {
    setUsedAiFilter(next);
    void load({ usedAi: next });
  }

  async function savePricing() {
    setBusy(true);
    try {
      const saved = await api<{ rates: ModelRate[] }>("/api/admin/analytics/pricing", {
        method: "PUT",
        body: JSON.stringify({ rates }),
      });
      setRates(saved.rates);
      setStatus("Pricing rates saved. New traces use these USD / 1M token rates.");
      await load();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Failed to save pricing");
    } finally {
      setBusy(false);
    }
  }

  function updateRate(index: number, patch: Partial<ModelRate>) {
    setRates((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  useEffect(() => { void load(); }, []);

  const totals = summary?.totals;
  const days = summary?.days ?? [];
  const maxDay = Math.max(1, ...days.map((d) => d.requests));
  const yTicks = [maxDay, Math.round(maxDay / 2), 0];
  const labelStep = days.length > 21 ? 3 : days.length > 14 ? 2 : 1;
  const hover = hoverDay ? days.find((d) => d.day === hoverDay) : null;

  return (
    <div className="stack">
      <section className="panel">
        <PanelHeader
          title="AI usage analytics"
          subtitle="Token/cost estimates and routing mix for assessment (super-admin only)."
        />
        <form
          className="analytics-range-bar"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
        >
          <label className="analytics-date-field">
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setActivePreset(null);
              }}
            />
          </label>
          <label className="analytics-date-field">
            <span>To</span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={isoDay(new Date())}
              onChange={(e) => {
                setToDate(e.target.value);
                setActivePreset(null);
              }}
            />
          </label>
          <div className="analytics-presets" role="group" aria-label="Quick ranges">
            {([7, 14, 30] as const).map((n) => (
              <button
                key={n}
                type="button"
                className={`chip filter${activePreset === n ? " selected" : ""}`}
                disabled={busy}
                onClick={() => applyPreset(n)}
              >
                {n}d
              </button>
            ))}
          </div>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Searching…" : "Search"}
          </button>
        </form>
        {status && <div className="inline-message">{status}</div>}
        <div className="metric-grid analytics-kpis">
          <Metric label="Requests" value={String(totals?.requests ?? 0)} detail={`${appliedFrom} → ${appliedTo}`} />
          <Metric label="LLM hit rate" value={`${totals?.aiHitRate ?? 0}%`} detail={`LLM ${totals?.aiRequests ?? 0} · Hybrid A ${totals?.nonAiRequests ?? 0}`} />
          <Metric
            label="Voyage embed hit"
            value={`${totals?.embedHitRate ?? 0}%`}
            detail={`${totals?.embedHits ?? 0} hits · ${totals?.embedTokens ?? 0} tokens`}
          />
          <Metric
            label="Billable total"
            value={`$${(totals?.costUsd ?? 0).toFixed(4)}`}
            detail={`LLM $${(totals?.llmCostUsd ?? 0).toFixed(4)} + Voyage $${(totals?.embedCostBillableUsd ?? 0).toFixed(4)}`}
          />
        </div>
        <div className="metric-grid analytics-kpis" style={{ marginTop: 12 }}>
          <Metric
            label="LLM tokens"
            value={String(totals?.totalTokens ?? 0)}
            detail={`in ${totals?.promptTokens ?? 0} · out ${totals?.completionTokens ?? 0}`}
          />
          <Metric
            label="Voyage list cost"
            value={`$${(totals?.embedCostListUsd ?? 0).toFixed(4)}`}
            detail="At $0.02 / 1M (before free tier)"
          />
          <Metric
            label="List total"
            value={`$${(totals?.costListUsd ?? 0).toFixed(4)}`}
            detail="LLM + Voyage at list prices"
          />
          <Metric
            label="Voyage free tier"
            value={summary?.voyageFreeTier?.stillOnFreeTier ? "ACTIVE" : "EXHAUSTED"}
            detail={
              summary?.voyageFreeTier
                ? `${(summary.voyageFreeTier.freeRemainingTokens / 1_000_000).toFixed(1)}M of ${(summary.voyageFreeTier.freeAllowanceTokens / 1_000_000).toFixed(0)}M free left · used ${(summary.voyageFreeTier.lifetimeEmbedTokens / 1_000_000).toFixed(2)}M`
                : "Lifetime tracked on analyze embeds"
            }
          />
        </div>
      </section>

      <section className="panel">
        <PanelHeader
          title="Model & embedding pricing"
          subtitle="LLM chat rates plus Voyage embed ($/1M tokens). Aliases map automatically. Embed output rate is unused (input-only)."
          action={<button className="primary" disabled={busy || rates.length === 0} onClick={() => void savePricing()}>Save rates</button>}
        />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Input $/1M</th>
                <th>Output $/1M</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r, i) => (
                <tr key={r.key}>
                  <td>
                    <strong>{r.label}</strong>
                    <div className="muted"><code>{r.key}</code></div>
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={r.promptPerMillion}
                      onChange={(e) => updateRate(i, { promptPerMillion: Number(e.target.value) })}
                      style={{ width: 120, font: "inherit", padding: "6px 8px", border: "1px solid var(--border-strong)", borderRadius: 8 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      value={r.completionPerMillion}
                      onChange={(e) => updateRate(i, { completionPerMillion: Number(e.target.value) })}
                      disabled={r.key.startsWith("voyage")}
                      style={{ width: 120, font: "inherit", padding: "6px 8px", border: "1px solid var(--border-strong)", borderRadius: 8 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted" style={{ marginTop: 12 }}>
          DeepSeek Flash $0.15 / $0.60 · GPT-4o Mini $0.15 / $0.60 · Voyage 4 Lite $0.02 / 1M (200M free tokens) —{" "}
          <a href="https://api-docs.deepseek.com/quick_start/pricing/" target="_blank" rel="noreferrer">DeepSeek</a>
          {" · "}
          <a href="https://developers.openai.com/api/docs/pricing" target="_blank" rel="noreferrer">OpenAI</a>
          {" · "}
          <a href="https://docs.voyageai.com/docs/pricing" target="_blank" rel="noreferrer">Voyage</a>.
        </p>
      </section>

      <section className="panel">
        <PanelHeader
          title="Daily trend"
          subtitle="Stacked volume by day — LLM judge vs Hybrid A, with Voyage hits on hover."
        />
        {days.length > 0 ? (
          <div className="analytics-chart" onMouseLeave={() => setHoverDay(null)}>
            <div className="analytics-chart-y" aria-hidden="true">
              {yTicks.map((tick) => (
                <span key={`y-${tick}`}>{tick}</span>
              ))}
            </div>
            <div className="analytics-chart-main">
              <div className="analytics-chart-grid" aria-hidden="true">
                <span /><span /><span />
              </div>
              <div className="analytics-chart-cols" style={{ ["--col-count" as string]: String(Math.max(1, days.length)) }}>
                {days.map((d, i) => {
                  const heightPct = d.requests > 0 ? Math.max(8, (d.requests / maxDay) * 100) : 0;
                  const aiShare = d.requests ? (d.aiRequests / d.requests) * 100 : 0;
                  const nonAiShare = d.requests ? (d.nonAiRequests / d.requests) * 100 : 0;
                  const showLabel = i % labelStep === 0 || i === days.length - 1;
                  return (
                    <button
                      key={d.day}
                      type="button"
                      className={`analytics-col${hoverDay === d.day ? " is-active" : ""}${d.requests === 0 ? " is-empty" : ""}`}
                      onMouseEnter={() => setHoverDay(d.day)}
                      onFocus={() => setHoverDay(d.day)}
                      aria-label={`${d.day}: ${d.requests} requests, ${d.aiRequests} LLM, ${d.nonAiRequests} Hybrid A`}
                    >
                      <div className="analytics-col-plot">
                        {d.requests > 0 ? (
                          <div className="analytics-col-stack" style={{ height: `${heightPct}%` }}>
                            <div className="analytics-col-seg nonai" style={{ height: `${nonAiShare}%` }} />
                            <div className="analytics-col-seg ai" style={{ height: `${aiShare}%` }} />
                          </div>
                        ) : (
                          <span className="analytics-col-zero" />
                        )}
                      </div>
                      <span className={`analytics-col-label${showLabel ? "" : " is-hidden"}`}>{d.day.slice(5)}</span>
                    </button>
                  );
                })}
              </div>
              {hover && (
                <div className="analytics-chart-tooltip" role="status">
                  <strong>{hover.day}</strong>
                  <span>{hover.requests} requests · LLM {hover.aiRequests} · Hybrid A {hover.nonAiRequests}</span>
                  <span>Voyage hits {hover.embedHits ?? 0} · ${Number(hover.costUsd ?? 0).toFixed(4)}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty">No daily data yet. Run Ask or /analyze to generate traces.</div>
        )}
        <div className="analytics-legend">
          <span className="swatch ai" /> LLM judge
          <span className="swatch nonai" /> Hybrid A
        </div>
      </section>

      <section className="panel">
        <PanelHeader title="Assessment traces" subtitle="Questions and answers retained 90 days" />
        <div className="faq-toolbar analytics-trace-toolbar">
          <input
            className="faq-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void load();
              }
            }}
            placeholder="Search question or answer…"
          />
          <div className="analytics-segment" role="tablist" aria-label="Route filter">
            {(
              [
                { id: "all", label: "All" },
                { id: "ai", label: "LLM" },
                { id: "non-ai", label: "Hybrid A" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={usedAiFilter === opt.id}
                className={usedAiFilter === opt.id ? "is-active" : undefined}
                disabled={busy}
                onClick={() => setRouteFilter(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button className="secondary" type="button" disabled={busy} onClick={() => void load()}>
            Apply
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Source</th>
                <th>Stay status</th>
                <th>Stay dates</th>
                <th>LLM</th>
                <th>Voyage</th>
                <th>LLM tok</th>
                <th>Embed tok</th>
                <th>LLM $</th>
                <th>Embed $</th>
                <th>Total $</th>
                <th>Question</th>
              </tr>
            </thead>
            <tbody>
              {traces.map((t) => {
                const llmCost = t.llmCostUsd ?? ((t.promptCostUsd ?? 0) + (t.completionCostUsd ?? 0));
                const embedList = t.embedCostListUsd ?? 0;
                const embedBill = t.embedCostBillableUsd ?? 0;
                const aiLabel = t.usedAi
                  ? (t.aiName || t.model || t.judgeProfileId || "AI")
                  : "No (hybrid A)";
                return (
                <Fragment key={t.id}>
                  <tr className="analytics-trace-row" onClick={() => setExpanded(expanded === t.id ? null : t.id)}>
                    <td>{new Date(t.createdAt).toLocaleString()}</td>
                    <td>
                      <div>{t.source}</div>
                      <StayChips compact propertyName={t.propertyName} />
                    </td>
                    <td>
                      {t.stayStatus
                        ? <span className="chip" style={{ fontSize: "0.7rem" }}>{stayStatusLabel(t.stayStatus)}</span>
                        : <span className="muted">—</span>}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{t.stayDates || <span className="muted">—</span>}</td>
                    <td>
                      <span className={`badge ${t.usedAi ? "warning" : "success"}`}>{aiLabel}</span>
                    </td>
                    <td>
                      <span className={`badge ${t.usedEmbed ? "warning" : "success"}`}>
                        {t.usedEmbed ? (t.embedModel || "voyage") : "No"}
                      </span>
                    </td>
                    <td>{t.promptTokens + t.completionTokens}</td>
                    <td>{t.embedTokens ?? 0}</td>
                    <td>${llmCost.toFixed(4)}</td>
                    <td title={`List $${embedList.toFixed(6)} · Billable $${embedBill.toFixed(6)}`}>
                      ${embedBill.toFixed(4)}
                      {embedList > 0 && embedBill === 0 ? <span className="muted"> (free)</span> : null}
                    </td>
                    <td>${t.costUsd.toFixed(4)}</td>
                    <td className="trace-q">{t.query.slice(0, 80)}{t.query.length > 80 ? "…" : ""}</td>
                  </tr>
                  {expanded === t.id && (
                    <tr className="analytics-trace-detail">
                      <td colSpan={12}>
                        <StayFields propertyName={t.propertyName} stayDates={t.stayDates} stayStatus={t.stayStatus} />
                        <div className="label">Question</div>
                        <div className="reply">{t.query}</div>
                        <div className="label" style={{ marginTop: 10 }}>Answer</div>
                        <div className="reply">{t.answer || "—"}</div>
                        <p className="muted">
                          {aiLabel} · mode {t.mode || "—"} · similarity {t.similarity}% · band {t.band}
                          · LLM in/out {t.promptTokens}/{t.completionTokens} (${llmCost.toFixed(6)})
                          · Voyage {t.usedEmbed ? "HIT" : "skip"} {t.embedTokens ?? 0} tok
                          {" "}(list ${embedList.toFixed(6)} / billable ${embedBill.toFixed(6)})
                          · total billable ${t.costUsd.toFixed(6)}
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
          </table>
          {traces.length === 0 && !busy && <div className="empty">No traces in range.</div>}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, detail }: { label: string; value: string; detail: string }) {
  return <div className="metric panel"><span>{label}</span><strong>{value}</strong><small>{detail}</small></div>;
}
function PanelHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="panel-header">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}
export { App };
